import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, TextField, MenuItem, Tooltip, IconButton, Tab, Tabs,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Accordion, AccordionSummary, AccordionDetails, Button,
} from '@mui/material';
import {
  BugReport, Refresh, ExpandMore, Warning,
  Error as ErrIcon, CheckCircle,
} from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const YEAR = new Date().getFullYear();

function apiFetch(path: string) {
  return fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const SEV_CFG = {
  critical: { color: '#b71c1c', bg: '#ffebee', label: 'Critical', icon: <ErrIcon sx={{ fontSize: 14 }} /> },
  high:     { color: '#e65100', bg: '#fff3e0', label: 'High',     icon: <Warning  sx={{ fontSize: 14 }} /> },
  medium:   { color: '#f57f17', bg: '#fffde7', label: 'Medium',   icon: <Warning  sx={{ fontSize: 14 }} /> },
  low:      { color: '#1565c0', bg: '#e3f2fd', label: 'Low',      icon: <CheckCircle sx={{ fontSize: 14 }} /> },
};

const TYPE_CFG: Record<string, { label: string; emoji: string }> = {
  large_salary_increase:     { label: 'Large Salary Increase',      emoji: '💸' },
  duplicate_bank_account:    { label: 'Duplicate Bank Account',     emoji: '🏦' },
  pay_after_termination:     { label: 'Pay After Termination',      emoji: '⚰️' },
  salary_below_grade_min:    { label: 'Below Grade Minimum',        emoji: '📉' },
  salary_above_grade_max:    { label: 'Above Grade Maximum',        emoji: '📈' },
  outlier_bonus:             { label: 'Outlier Bonus',              emoji: '🎁' },
  stale_salary:              { label: 'Stale Salary (>24mo)',       emoji: '🕰️' },
  missing_mpf:               { label: 'Missing MPF Record',         emoji: '❌' },
  duplicate_payroll_item:    { label: 'Duplicate Payroll Item',     emoji: '👥' },
  terminated_still_enrolled: { label: 'Terminated — Still Enrolled', emoji: '🚫' },
};

interface Anomaly {
  id: string; type: string; severity: 'critical' | 'high' | 'medium' | 'low';
  employeeId?: string; employeeName?: string; department?: string;
  description: string; detail?: string; amount?: number; currency?: string;
  detectedAt: string; reference?: string;
}

function AnomalyRow({ a, dismissed, onDismiss }: {
  a: Anomaly; dismissed: boolean; onDismiss: () => void;
}) {
  const sc = SEV_CFG[a.severity];
  const tc = TYPE_CFG[a.type] ?? { label: a.type, emoji: '⚠️' };
  return (
    <TableRow sx={{ opacity: dismissed ? 0.4 : 1, bgcolor: dismissed ? '#f9f9f9' : undefined }}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ color: sc.color }}>{sc.icon}</Box>
          <Chip size="small" label={sc.label}
            sx={{ bgcolor: sc.bg, color: sc.color, fontSize: '0.6rem' }} />
        </Box>
      </TableCell>
      <TableCell>
        <Chip size="small" label={`${tc.emoji} ${tc.label}`}
          sx={{ bgcolor: '#f5f5f5', fontSize: '0.6rem', maxWidth: 180 }} />
      </TableCell>
      <TableCell>
        {a.employeeName
          ? <Box>
              <Typography variant="body2" fontWeight={600}>{a.employeeName}</Typography>
              <Typography variant="caption" color="text.secondary">{a.department}</Typography>
            </Box>
          : <Typography variant="caption" color="text.disabled">N/A</Typography>
        }
      </TableCell>
      <TableCell>
        <Typography variant="body2">{a.description}</Typography>
        {a.detail && <Typography variant="caption" color="text.secondary">{a.detail}</Typography>}
      </TableCell>
      <TableCell align="right">
        {a.amount ? (
          <Typography variant="body2" fontWeight={600} color="#C08A2E">
            {a.currency ?? 'HKD'} {a.amount.toLocaleString('en-HK', { maximumFractionDigits: 0 })}
          </Typography>
        ) : '—'}
      </TableCell>
      <TableCell>
        <Tooltip title={dismissed ? 'Dismissed' : 'Dismiss this finding'}>
          <IconButton size="small" onClick={onDismiss} sx={{ opacity: dismissed ? 0.5 : 1 }}>
            <CheckCircle fontSize="small" sx={{ color: dismissed ? '#2e7d32' : '#9e9e9e' }} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

export default function PayrollAnomalies() {
  const [year, setYear]       = useState(YEAR);
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState(0);
  const [error, setError]     = useState('');
  const [sevFilter, setSev]   = useState('');
  const [typeFilter, setType] = useState('');
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await apiFetch(`/api/payroll-anomalies?year=${year}`)); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const dismiss = (id: string) => setDismissed(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const anomalies: Anomaly[] = data?.anomalies ?? [];
  const summary = data?.summary ?? {};

  const active   = anomalies.filter(a => !dismissed.has(a.id));
  const filtered = active.filter(a =>
    (!sevFilter  || a.severity === sevFilter) &&
    (!typeFilter || a.type     === typeFilter)
  );

  const criticals = active.filter(a => a.severity === 'critical');

  // Group for "by type" accordion view
  const byType: Record<string, Anomaly[]> = {};
  filtered.forEach(a => {
    if (!byType[a.type]) byType[a.type] = [];
    byType[a.type].push(a);
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#b71c1c', width: 48, height: 48 }}><BugReport /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Payroll Anomaly Detection</Typography>
            <Typography variant="body2" color="text.secondary">
              Automated scan for suspicious payroll patterns and data quality issues
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField select size="small" label="Year" value={year}
            onChange={e => setYear(parseInt(e.target.value))} sx={{ width: 90 }}>
            {[YEAR - 1, YEAR, YEAR + 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <Tooltip title="Re-scan"><IconButton onClick={load} disabled={loading}><Refresh /></IconButton></Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Summary KPIs */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              { label: 'Total Findings',   value: active.length,                       color: '#37474f' },
              { label: '🔴 Critical',      value: active.filter(a => a.severity === 'critical').length, color: '#b71c1c' },
              { label: '🟠 High',          value: active.filter(a => a.severity === 'high').length,     color: '#e65100' },
              { label: '🟡 Medium',        value: active.filter(a => a.severity === 'medium').length,   color: '#f57f17' },
              { label: '🔵 Low',           value: active.filter(a => a.severity === 'low').length,      color: '#1565c0' },
              { label: '✅ Dismissed',     value: dismissed.size,                       color: '#2e7d32' },
            ].map(k => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={k.label}>
                <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${k.color}` }}>
                  <Typography variant="h5" fontWeight={700} color={k.color}>{k.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {criticals.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>{criticals.length} critical finding{criticals.length > 1 ? 's' : ''} require immediate attention:</strong>{' '}
              {criticals.map(c => TYPE_CFG[c.type]?.label ?? c.type).join(', ')}
            </Alert>
          )}

          {active.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 64, color: '#2e7d32', mb: 1 }} />
              <Typography variant="h6" color="#2e7d32">No anomalies detected</Typography>
              <Typography variant="body2" color="text.secondary">Payroll data looks clean for {year}.</Typography>
            </Paper>
          )}

          {active.length > 0 && (
            <>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}>
                <Tab label={`All Findings (${active.length})`} />
                <Tab label="By Type" />
              </Tabs>

              {/* Filters */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <TextField select size="small" label="Severity" value={sevFilter}
                  onChange={e => setSev(e.target.value)} sx={{ minWidth: 120 }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="critical">🔴 Critical</MenuItem>
                  <MenuItem value="high">🟠 High</MenuItem>
                  <MenuItem value="medium">🟡 Medium</MenuItem>
                  <MenuItem value="low">🔵 Low</MenuItem>
                </TextField>
                <TextField select size="small" label="Type" value={typeFilter}
                  onChange={e => setType(e.target.value)} sx={{ minWidth: 220 }}>
                  <MenuItem value="">All Types</MenuItem>
                  {Object.entries(TYPE_CFG).map(([k,v]) => <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>)}
                </TextField>
                {dismissed.size > 0 && (
                  <Button size="small" variant="outlined" onClick={() => setDismissed(new Set())}
                    sx={{ textTransform: 'none', borderColor: '#e0e0e0' }}>
                    Restore {dismissed.size} dismissed
                  </Button>
                )}
              </Box>

              {tab === 0 && (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Severity</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Employee</TableCell>
                        <TableCell>Finding</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Dismiss</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filtered.map(a => (
                        <AnomalyRow key={a.id} a={a}
                          dismissed={dismissed.has(a.id)}
                          onDismiss={() => dismiss(a.id)} />
                      ))}
                      {filtered.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>No findings match the current filters.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {tab === 1 && (
                <Box>
                  {Object.entries(byType).map(([type, items]) => {
                    const tc = TYPE_CFG[type] ?? { emoji: '⚠️', label: type };
                    const worstSev = items[0]?.severity ?? 'low';
                    const sc = SEV_CFG[worstSev];
                    return (
                      <Accordion key={type} defaultExpanded={worstSev === 'critical'}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                            <Typography sx={{ fontSize: '1.2rem' }}>{tc.emoji}</Typography>
                            <Typography fontWeight={700}>{tc.label}</Typography>
                            <Chip size="small" label={items.length}
                              sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 700 }} />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Severity</TableCell>
                                  <TableCell>Employee</TableCell>
                                  <TableCell>Finding</TableCell>
                                  <TableCell align="right">Amount</TableCell>
                                  <TableCell>Dismiss</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map(a => (
                                  <AnomalyRow key={a.id} a={a}
                                    dismissed={dismissed.has(a.id)}
                                    onDismiss={() => dismiss(a.id)} />
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
